import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { useTranslation, Trans } from 'react-i18next';
import { Btn, Tag, Card, Avatar, QGlyph, Icons, ThemeToggle } from './ui';
import { LangSwitcher } from './LangSwitcher';

interface PublicAnswer {
  id: number;
  server: string;
  user_id: string;
  question: string;
  answer: string;
  created_at: string;
  answerd_at: string;
  nsfw: boolean;
  questioner_user_id: string;
  questioner_server: string;
  not_anonymous: boolean;
}

const UTCtoJST = (utcDate: string): string =>
  moment.utc(utcDate).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');

// ─── Nav ─────────────────────────────────────────────────────────────────────

const Nav: React.FC<{ loggedIn: boolean }> = ({ loggedIn }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <header className="flex items-center justify-between px-4 sm:px-10 py-4 sm:py-6">
      <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-7 select-none dark:hidden" />
      <img src="/fediqb_banner.svg" alt="FediQB" className="h-7 select-none hidden dark:block" />
      <nav className="hidden md:flex items-center gap-1 text-sm text-ink-600 dark:text-ink-300">
        {[
          { l: t('nav.publicQuestions'), h: '#browse' },
          { l: t('nav.features'), h: '#features' },
          { l: t('nav.howTo'), h: '#how' },
        ].map((it) => (
          <a
            key={it.l}
            className="px-4 py-2 rounded-full hover:bg-ink-100 dark:hover:bg-ink-800/60 transition"
            href={it.h}
          >
            {it.l}
          </a>
        ))}
      </nav>
      <div className="flex items-center gap-2">
        <LangSwitcher />
        <ThemeToggle />
        {loggedIn ? (
          <Btn variant="primary" size="md" onClick={() => navigate('/mypage')}>
            {t('common.mypage')}
          </Btn>
        ) : (
          <>
            <Btn variant="ghost" size="md" onClick={() => navigate('/login')}>
              {t('common.login')}
            </Btn>
            <Btn variant="primary" size="md" onClick={() => navigate('/login')}>
              {t('nav.makeBox')}
            </Btn>
          </>
        )}
      </div>
    </header>
  );
};

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero: React.FC<{ loggedIn: boolean }> = ({ loggedIn }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <section className="relative px-4 sm:px-10 pt-8 sm:pt-10 pb-16 sm:pb-20 overflow-hidden">
      <div
        className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full opacity-50"
        style={{ background: 'radial-gradient(closest-side, rgba(54,149,211,0.35), transparent)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-20 right-0 w-[500px] h-[500px] rounded-full opacity-50"
        style={{ background: 'radial-gradient(closest-side, rgba(90,179,49,0.28), transparent)' }}
      />
      <div className="relative max-w-5xl mx-auto grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 md:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6">
            <Tag tone="green">
              <Icons.Sparkle width={12} height={12} /> {t('top.tagline')}
            </Tag>
            <Tag tone="blue">ActivityPub</Tag>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-[62px] leading-[1.05] font-extrabold tracking-tight">
            {/* @ts-ignore - Trans typings incompatible with TS 4.9 */}
            <Trans i18nKey="top.heroTitle" components={{
              blue: <span className="text-brand-blue-500" />,
              green: <span className="text-brand-green-500" />,
              br: <br />
            }} />
          </h1>
          <p className="mt-6 text-[17px] leading-relaxed text-ink-600 dark:text-ink-300 max-w-[520px]">
            {t('top.heroDesc')}
          </p>
          <div className="mt-9 flex items-center gap-3 flex-wrap">
            <Btn variant="primary" size="xl" onClick={() => navigate(loggedIn ? '/mypage' : '/login')}>
              <Icons.Plus width={18} height={18} /> {t('top.ctaStart')}
            </Btn>
            <Btn variant="outline" size="xl" as="a" href="#how">
              {t('top.ctaHowTo')}
            </Btn>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-ink-500 dark:text-ink-400 flex-wrap">
            <div className="flex items-center gap-2">
              <Icons.Check width={16} height={16} className="text-brand-green-500" /> {t('top.badgeFree')}
            </div>
            <div className="flex items-center gap-2">
              <Icons.Check width={16} height={16} className="text-brand-green-500" /> {t('top.badgeAdFree')}
            </div>
            <div className="flex items-center gap-2">
              <Icons.Lock width={16} height={16} className="text-brand-blue-500" /> {t('top.badgeAnonymous')}
            </div>
          </div>
        </div>
        <div className="hidden md:block col-span-5 relative h-[420px]">
          <div className="absolute top-2 right-8 w-[340px] rotate-[-3deg]">
            <Card className="!p-5">
              <div className="flex items-start gap-3">
                <Avatar anonymous size={36} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-semibold text-ink-500">{t('common.anonymous')}</span>
                    <Tag tone="outline">Q</Tag>
                  </div>
                  <p className="text-[15px] leading-relaxed">
                    {t('top.demoQ')}
                  </p>
                </div>
              </div>
            </Card>
          </div>
          <div className="absolute top-[160px] right-0 w-[380px] rotate-[2deg]">
            <Card className="!p-5 ring-2 ring-brand-blue-200 dark:ring-brand-blue-900/60">
              <div className="flex items-start gap-3">
                <QGlyph tone="blue" size={36} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-semibold text-brand-blue-600">{t('common.answer')}</span>
                    <Tag tone="blue">
                      <Icons.Mastodon width={11} height={11} /> mastodon
                    </Tag>
                  </div>
                  <p className="text-[15px] leading-relaxed">
                    {t('top.demoA')}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-[12px] text-ink-500">
                    <span className="inline-flex items-center gap-1">
                      <Icons.Boost width={12} height={12} /> 14
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Icons.Heart width={12} height={12} /> 38
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Features ────────────────────────────────────────────────────────────────

const Features: React.FC = () => {
  const { t } = useTranslation();
  const features = [
    {
      g: 'blue',
      title: t('top.f1Title'),
      body: t('top.f1Body'),
      Icon: Icons.Lock,
    },
    {
      g: 'green',
      title: t('top.f2Title'),
      body: t('top.f2Body'),
      Icon: Icons.Globe,
    },
    {
      g: 'blue',
      title: t('top.f3Title'),
      body: t('top.f3Body'),
      Icon: Icons.Compass,
    },
    {
      g: 'green',
      title: t('top.f4Title'),
      body: t('top.f4Body'),
      Icon: Icons.Sparkle,
    },
  ];
  return (
    <section id="features" className="px-4 sm:px-10 py-12 sm:py-16 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
        <div>
          <Tag tone="green" className="mb-3">FEATURES</Tag>
          <h2 className="text-4xl font-extrabold tracking-tight">
            {t('top.featureTitle')}
          </h2>
        </div>
        <p className="max-w-md text-ink-600 dark:text-ink-300 leading-relaxed">
          {t('top.featureDesc')}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {features.map((f, i) => {
          const bg =
            f.g === 'blue'
              ? 'bg-brand-blue-50 text-brand-blue-600 dark:bg-brand-blue-900/40 dark:text-brand-blue-200'
              : 'bg-brand-green-50 text-brand-green-600 dark:bg-brand-green-900/40 dark:text-brand-green-200';
          return (
            <Card key={i} hover className="!p-7">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${bg}`}>
                <f.Icon width={22} height={22} />
              </div>
              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-[13.5px] leading-relaxed text-ink-600 dark:text-ink-300">{f.body}</p>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

// ─── HowItWorks ──────────────────────────────────────────────────────────────

const APP_URL = process.env.REACT_APP_APP_URL ?? 'https://fediqb.y-zu.org';

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  const steps = [
    { n: t('top.step1Num'), t: t('top.step1Title'), d: t('top.step1Desc') },
    { n: t('top.step2Num'), t: t('top.step2Title'), d: t('top.step2Desc', { appUrl: APP_URL }) },
    { n: t('top.step3Num'), t: t('top.step3Title'), d: t('top.step3Desc') },
  ];
  return (
    <section id="how" className="px-4 sm:px-10 py-12 sm:py-16 max-w-6xl mx-auto">
      <div className="bg-brand-blue-50/40 dark:bg-brand-blue-900/10 rounded-2xl sm:rounded-[40px] p-6 sm:p-12">
        <div className="mb-10">
          <Tag tone="blue" className="mb-3">HOW IT WORKS</Tag>
          <h2 className="text-4xl font-extrabold tracking-tight">{t('top.howTitle')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i}>
              <div className="text-[72px] font-extrabold text-brand-blue-200 dark:text-brand-blue-900/60 leading-none mb-2 font-mono">
                {s.n}
              </div>
              <h3 className="text-xl font-bold mb-2">{s.t}</h3>
              <p className="text-[14px] leading-relaxed text-ink-600 dark:text-ink-300 max-w-xs">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── PublicFeed ──────────────────────────────────────────────────────────────

const PublicFeed: React.FC<{ answers: PublicAnswer[]; nsfwDisplay: boolean }> = ({
  answers,
  nsfwDisplay,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const visible = answers.filter((a) => nsfwDisplay || !a.nsfw);
  return (
    <section id="browse" className="px-4 sm:px-10 py-12 sm:py-16 max-w-6xl mx-auto">
      <div className="mb-8">
        <Tag tone="blue" className="mb-3">BROWSE</Tag>
        <h2 className="text-4xl font-extrabold tracking-tight">{t('top.browseTitle')}</h2>
      </div>
      <div className="space-y-4">
        {visible.length === 0 && (
          <p className="text-ink-500 dark:text-ink-400 text-center py-10">
            {t('top.browseEmpty')}
          </p>
        )}
        {visible.map((a) => (
          <Card key={a.id} className="!p-5">
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                {a.not_anonymous ? (
                  <button
                    onClick={() => navigate(`/questionbox/@${a.questioner_user_id}@${a.questioner_server}`)}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <Avatar seed={a.questioner_user_id || 'A'} size={20} />
                    <span className="text-[12px] text-ink-500 dark:text-ink-400">
                      @{a.questioner_user_id}@{a.questioner_server}
                    </span>
                  </button>
                ) : (
                  <>
                    <Avatar anonymous={true} seed="A" size={20} />
                    <span className="text-[12px] text-ink-500 dark:text-ink-400">{t('common.anonymous')}</span>
                  </>
                )}
                {a.nsfw && (
                  <Tag tone="outline" className="ml-auto !text-rose-500 !border-rose-200">
                    NSFW
                  </Tag>
                )}
              </div>
              <p className="text-[15px] font-semibold leading-snug">{a.question}</p>
            </div>
            <div className="border-t border-dashed border-ink-150 dark:border-ink-800 pt-3 flex items-start gap-3">
              <QGlyph tone="blue" size={24} />
              <p className="text-[14px] leading-relaxed text-ink-700 dark:text-ink-200 flex-1">
                {a.answer}
              </p>
            </div>
            <div className="mt-3 flex items-center gap-4 text-[12px] text-ink-500 dark:text-ink-400">
              <button
                onClick={() => navigate(`/questionbox/@${a.user_id}@${a.server}`)}
                className="hover:text-brand-blue-500 hover:underline font-mono"
              >
                @{a.user_id}@{a.server}
              </button>
              <span className="ml-auto">{UTCtoJST(a.answerd_at)}</span>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

// ─── SisterServices ──────────────────────────────────────────────────────────

const SisterServices: React.FC = () => {
  const { t } = useTranslation();
  const services = [
    {
      name: 'Fediverseプロフ帳',
      desc: t('top.sisterPurofu'),
      url: 'https://purofu.y-zu.org',
      bg: 'bg-brand-blue-50 dark:bg-brand-blue-900/40',
      logo: '/purofu_logo.png',
    },
    {
      name: 'tootori',
      desc: t('top.sisterTootori'),
      url: 'https://tootori.app',
      bg: 'bg-[#c43a61]/10 dark:bg-[#c43a61]/20',
      logo: '/tootori_logo.svg',
    },
  ];

  return (
    <section className="px-4 sm:px-10 py-8 max-w-6xl mx-auto">
      <div className="mb-4">
        <Tag tone="green" className="mb-2">{t('top.sisterTag')}</Tag>
        <h2 className="text-2xl font-extrabold tracking-tight">{t('top.sisterTitle')}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((s) => {
          return (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card hover className="!p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center overflow-hidden p-1.5 ${s.bg}`}>
                  <img src={s.logo} alt={s.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold">{s.name}</h3>
                  <p className="text-[13px] text-ink-500 dark:text-ink-400 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
                <Icons.ExternalLink
                  width={16}
                  height={16}
                  className="text-ink-400 dark:text-ink-500 shrink-0"
                />
              </Card>
            </a>
          );
        })}
      </div>
    </section>
  );
};

// ─── Compat ───────────────────────────────────────────────────────────────────

const Compat: React.FC = () => {
  const { t } = useTranslation();
  const platforms = [
    { name: t('top.mastodon'), auth: t('top.mastodonApi'), blue: true },
    { name: t('top.pleroma'), auth: t('top.mastodonApi'), blue: true },
    { name: t('top.hollo'), auth: t('top.mastodonApi'), blue: true },
    { name: t('top.misskey'), auth: t('top.mauth'), blue: false },
  ];
  return (
    <section className="px-4 sm:px-10 py-12 sm:py-16 max-w-4xl mx-auto text-center">
      <Tag tone="green" className="mb-3">COMPATIBLE</Tag>
      <h2 className="text-3xl font-extrabold tracking-tight mb-3">{t('top.compatTitle')}</h2>
      <p className="text-ink-600 dark:text-ink-300">
        {t('top.compatDesc')}
      </p>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        {platforms.map((p) => (
          <div
            key={p.name}
            className="h-20 rounded-2xl bg-white dark:bg-ink-900 border border-ink-150 dark:border-ink-800 flex flex-col items-center justify-center gap-1.5 hover:shadow-card transition"
          >
            <span className="font-bold text-sm">{p.name}</span>
            <span className={`text-[10px] font-mono ${p.blue ? 'text-brand-blue-500' : 'text-brand-green-500'}`}>
              {p.auth}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── Creator ─────────────────────────────────────────────────────────────────

const Creator: React.FC = () => {
  return (
    <section className="px-4 sm:px-10 py-12 sm:py-16 max-w-6xl mx-auto text-center">
      <div className="mb-4">
        <Tag tone="blue" className="mb-2">CREATOR</Tag>
        <h2 className="text-2xl font-extrabold tracking-tight">作った人</h2>
      </div>
      <Card className="!p-6 max-w-sm mx-auto">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-2xl overflow-hidden bg-ink-100 dark:bg-ink-800 shrink-0">
            <img
              src="/yoheizuho.jpeg"
              alt="もちもちずきん"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-lg font-bold">もちもちずきん</h3>
            <a
              href="https://mstdn.y-zu.org/@Yohei_Zuho"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[13px] text-ink-500 dark:text-ink-400 font-mono hover:text-brand-blue-500 hover:underline transition"
            >
              @Yohei_Zuho@mstdn.y-zu.org
            </a>
          </div>
        </div>
      </Card>
    </section>
  );
};

// ─── Footer ──────────────────────────────────────────────────────────────────

const Footer: React.FC = () => {
  const { t } = useTranslation();
  return (
    <footer className="px-4 sm:px-10 py-8 sm:py-10 mt-8 border-t border-ink-150 dark:border-ink-800">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-5 select-none dark:hidden" />
          <img src="/fediqb_banner.svg" alt="FediQB" className="h-5 select-none hidden dark:block" />
          <span className="text-xs text-ink-400">© 2026 FediQB project</span>
        </div>
        <div className="flex items-center gap-5 text-sm text-ink-500 dark:text-ink-400 flex-wrap">
          <a
            href="https://gist.github.com/YoheiZuho/2fa69f3c5095bcc65319e9c89324055c"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-ink-800 dark:hover:text-ink-100"
          >
            {t('top.footerPrivacy')}
          </a>
          <a
            href="https://gist.github.com/YoheiZuho/f08a8a9a1b79b794c843996739060408"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-ink-800 dark:hover:text-ink-100"
          >
            {t('top.footerRule')}
          </a>
          <a
            href="https://mstdn.y-zu.org/@fediqb"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-ink-800 dark:hover:text-ink-100"
          >
            {t('top.footerAccount')}
          </a>
        </div>
      </div>
    </footer>
  );
};

// ─── Top ──────────────────────────────────────────────────────────────────────

const Top: React.FC = () => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<PublicAnswer[]>([]);
  const [nsfwDisplay, setNsfwDisplay] = useState(false);
  const [loggedIn, setLoggedIn] = useState(!!Cookies.get('jwtToken'));

  useEffect(() => {
    fetch('/api/v1/profile')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => setLoggedIn(!!data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/v1/list/answer/public')
      .then((r) => r.ok ? r.json() : [])
      .then(setAnswers)
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      <Nav loggedIn={loggedIn} />
      <Hero loggedIn={loggedIn} />
      <Features />
      <HowItWorks />
      <section className="px-4 sm:px-10 py-4 max-w-6xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl px-5 py-3 flex items-start gap-3">
          <Icons.Flag width={16} height={16} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-[13px] text-amber-800 dark:text-amber-200">
            {t('top.warningRule')}
            <a
              href="https://gist.github.com/YoheiZuho/f08a8a9a1b79b794c843996739060408#file-fediqbrule-md"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {t('top.warningRuleLink')}
            </a>
            {t('top.warningRuleEnd')}
          </p>
        </div>
      </section>
      <div className="px-4 sm:px-10 py-4 max-w-6xl mx-auto flex items-center gap-3">
        <label className="flex items-center gap-2 text-[13px] text-ink-600 dark:text-ink-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={nsfwDisplay}
            onChange={(e) => setNsfwDisplay(e.target.checked)}
            className="rounded"
          />
          {t('top.showNsfw')}
        </label>
      </div>
      <PublicFeed answers={answers} nsfwDisplay={nsfwDisplay} />
      <Compat />
      <SisterServices />
      <Creator />
      <Footer />
    </div>
  );
};

export default Top;
